'use client';

import * as React from 'react';
import { motion, useInView } from 'motion/react';

import { AccountMonitoringScene } from './account-monitoring-scene';
import { CompetitiveWatchScene } from './competitive-watch-scene';

export type OperationalPreviewNumber = '04' | '05' | '06';

export const OPERATIONAL_PREVIEW_COPY = {
  '04': {
    eyebrow: 'Continuous account monitoring',
    title: 'Keep up with changes across your accounts.'
  },
  '05': {
    eyebrow: 'Competitive watch',
    title: 'See what competitors are saying—and who’s paying attention.'
  },
  '06': {
    eyebrow: 'Churn detection',
    title: "Spot customer risk while there's time to act."
  }
} as const;

type SceneProps = { reduced: boolean; ready: boolean };

function Reveal({
  children,
  reduced,
  ready,
  delay = 0,
  className = ''
}: SceneProps & {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}): React.JSX.Element {
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 10 }}
      animate={{
        opacity: ready || reduced ? 1 : 0,
        y: ready || reduced ? 0 : 10
      }}
      transition={
        reduced
          ? { duration: 0 }
          : {
              duration: 0.65,
              delay: ready ? delay : 0,
              ease: [0.22, 1, 0.36, 1]
            }
      }
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Arrow({ className = '' }: { className?: string }): React.JSX.Element {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`size-4 shrink-0 ${className}`}
    >
      <path d="M4 12h15m-6-6 6 6-6 6" />
    </svg>
  );
}

function SmallLabel({
  children
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <p className="font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-[color:var(--ink-3)]">
      {children}
    </p>
  );
}

function RelationshipNode({
  initials,
  name,
  detail,
  muted = false,
  dashed = false
}: {
  initials: string;
  name: string;
  detail: string;
  muted?: boolean;
  dashed?: boolean;
}): React.JSX.Element {
  return (
    <div
      className={`relative flex w-full items-center gap-2.5 rounded-xl border px-3 py-3 md:px-4 ${dashed ? 'border-dashed border-[color:var(--accent-deep)] bg-[color:var(--paper)]' : 'border-[color:var(--hairline)] bg-[color:var(--paper)]'}`}
    >
      <span
        aria-hidden
        className={`grid size-9 shrink-0 place-items-center rounded-lg font-mono text-[11px] ${muted ? 'bg-[color:var(--paper-3)] text-[color:var(--ink-4)]' : 'bg-[color:var(--mint)] text-[color:var(--accent-darker)]'}`}
      >
        {initials}
      </span>
      <div className="min-w-0">
        <p
          className={`text-[12px] font-medium ${muted ? 'text-[color:var(--ink-4)]' : 'text-[color:var(--ink)]'}`}
        >
          {name}
        </p>
        <p className="mt-1 text-[10px] leading-tight text-[color:var(--ink-4)]">
          {detail}
        </p>
      </div>
    </div>
  );
}

function RetentionScene(props: SceneProps): React.JSX.Element {
  return (
    <div className="flex h-full flex-col px-5 pb-6 pt-2 sm:px-8 md:px-10 md:pb-8 md:pt-4">
      <Reveal {...props}>
        <SmallLabel>Customer account</SmallLabel>
        <h3 className="mt-2 max-w-[23ch] text-[23px] font-medium leading-[1.2] tracking-[-0.03em] text-[color:var(--ink)] md:max-w-none md:text-[27px]">
          Your champion changed roles.
        </h3>
      </Reveal>

      <div className="relative my-5 flex-1 rounded-2xl bg-[color:var(--paper-2)] px-4 py-4 md:my-6 md:px-5 md:py-4">
        <Reveal
          {...props}
          delay={0.2}
          className="mx-auto w-fit"
        >
          <div className="flex items-center gap-2 font-mono text-[10px] font-medium text-[color:var(--ink-3)]">
            <span
              aria-hidden
              className="grid size-6 place-items-center rounded-md bg-[color:var(--mint-2)] text-[10px] text-[color:var(--accent-darker)]"
            >
              H
            </span>
            Hearth Computing
          </div>
        </Reveal>

        <svg
          aria-hidden
          viewBox="0 0 540 44"
          preserveAspectRatio="none"
          className="mx-auto hidden h-10 w-full max-w-[540px] overflow-visible text-[color:var(--ink-5)] md:block"
        >
          <motion.path
            d="M270 0V15H130V44"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="4 5"
            initial={props.reduced ? false : { opacity: 0 }}
            animate={{ opacity: props.ready || props.reduced ? 0.6 : 0 }}
            transition={{
              duration: props.reduced ? 0 : 0.5,
              delay: props.reduced ? 0 : 0.5
            }}
          />
          <motion.path
            d="M270 0V15H410V44"
            fill="none"
            stroke="var(--accent-deep)"
            strokeWidth="1"
            initial={props.reduced ? false : { pathLength: 0, opacity: 0 }}
            animate={{
              pathLength: props.ready || props.reduced ? 1 : 0,
              opacity: props.ready || props.reduced ? 1 : 0
            }}
            transition={{
              duration: props.reduced ? 0 : 0.65,
              delay: props.reduced ? 0 : 1
            }}
          />
        </svg>

        <div className="relative mt-4 grid gap-3 md:mx-auto md:mt-0 md:max-w-[540px] md:grid-cols-2 md:gap-5">
          <Reveal
            {...props}
            delay={0.65}
          >
            <RelationshipNode
              initials="TL"
              name="Theo Lane"
              detail="Former champion · left company"
              muted
            />
            <p className="mt-2 px-1 text-[10px] text-[color:var(--ink-4)]">
              Role change · public profile · Sep 01
            </p>
          </Reveal>
          <Reveal
            {...props}
            delay={1.35}
          >
            <RelationshipNode
              initials="AM"
              name="Amira Malik"
              detail="Operations lead"
              dashed
            />
          </Reveal>
        </div>
      </div>

      <Reveal
        {...props}
        delay={1.95}
        className="flex items-start gap-3 md:gap-5"
      >
        <span
          aria-hidden
          className="mt-0.5 hidden size-7 shrink-0 place-items-center rounded-full bg-[color:var(--mint)] text-[color:var(--accent-darker)] md:grid"
        >
          <Arrow />
        </span>
        <div>
          <p className="text-[14px] font-medium tracking-[-0.015em] text-[color:var(--ink)] md:text-[16px]">
            Confirm who owns the account relationship now.
          </p>
          <p className="mt-1.5 max-w-[58ch] text-[11px] leading-relaxed text-[color:var(--ink-4)] md:text-[12px]">
            A role change alone does not establish churn.
          </p>
        </div>
      </Reveal>
    </div>
  );
}

const SCENES = {
  '04': AccountMonitoringScene,
  '05': CompetitiveWatchScene,
  '06': RetentionScene
} as const;

export function OperationalIntelligenceCanvas({
  previewNumber,
  reduced
}: {
  previewNumber: OperationalPreviewNumber;
  reduced: boolean;
}): React.JSX.Element {
  const stageRef = React.useRef<HTMLDivElement>(null);
  const visible = useInView(stageRef, { once: true, amount: 0.25 });
  const [replay, setReplay] = React.useState(0);
  const [monitoringPaused, setMonitoringPaused] = React.useState(false);
  const [monitoringAdvance, setMonitoringAdvance] = React.useState(0);
  const Scene = SCENES[previewNumber];

  return (
    <div
      ref={stageRef}
      data-operational-preview={previewNumber}
      className={`flex min-w-0 flex-col overflow-hidden md:h-[456px] ${previewNumber === '04' ? 'h-[576px] min-[375px]:h-[512px]' : 'h-[640px] min-[375px]:h-[568px]'}`}
    >
      <div className="flex h-[48px] shrink-0 items-center justify-between gap-2 px-3 min-[375px]:px-5 sm:px-8 md:px-10">
        <p className="text-[10px] leading-relaxed text-[color:var(--ink-4)]">
          Illustrative example
        </p>
        {previewNumber === '04' ? (
          <div className="-mr-2 flex shrink-0 items-center gap-0.5">
            {!reduced && (
              <button
                type="button"
                onClick={() => setMonitoringPaused((value) => !value)}
                aria-label={
                  monitoringPaused
                    ? 'Play account updates'
                    : 'Pause account updates'
                }
                title={
                  monitoringPaused
                    ? 'Play account updates'
                    : 'Pause account updates'
                }
                className="grid size-11 place-items-center rounded-md text-[color:var(--ink-4)] transition-colors hover:bg-[color:var(--paper-2)] hover:text-[color:var(--ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-deep)] motion-reduce:transition-none"
              >
                <svg
                  aria-hidden
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  className="size-3.5"
                >
                  {monitoringPaused ? (
                    <path d="m7 4 9 6-9 6V4Z" />
                  ) : (
                    <path d="M7 4v12M13 4v12" />
                  )}
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setMonitoringPaused(true);
                setMonitoringAdvance((value) => value + 1);
              }}
              aria-label="Show next account update"
              className="flex h-11 items-center gap-1 rounded-md px-2 text-[10px] text-[color:var(--ink-4)] transition-colors hover:bg-[color:var(--paper-2)] hover:text-[color:var(--ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-deep)] motion-reduce:transition-none"
            >
              Next update
              <svg
                aria-hidden
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                className="size-3"
              >
                <path d="m7 5 5 5-5 5" />
              </svg>
            </button>
          </div>
        ) : (
          !reduced && (
            <button
              type="button"
              onClick={() => setReplay((value) => value + 1)}
              aria-label={`Replay ${OPERATIONAL_PREVIEW_COPY[previewNumber].eyebrow.toLowerCase()} preview`}
              className="group -mr-2 flex min-h-9 shrink-0 items-center gap-1.5 rounded-md px-2 text-[10px] text-[color:var(--ink-4)] transition-colors hover:bg-[color:var(--paper-2)] hover:text-[color:var(--ink)] active:bg-[color:var(--paper-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-deep)] focus-visible:ring-offset-2"
            >
              <svg
                aria-hidden
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-3.5 transition-transform group-hover:-rotate-45"
              >
                <path d="M4 8a6 6 0 1 1 .3 5M4 3.5V8h4.5" />
              </svg>
              Replay
            </button>
          )
        )}
      </div>
      <div
        key={`${previewNumber}-${replay}`}
        className="min-h-0 flex-1"
      >
        {previewNumber === '04' ? (
          <AccountMonitoringScene
            reduced={reduced}
            ready={visible}
            paused={monitoringPaused}
            advanceToken={monitoringAdvance}
          />
        ) : (
          <Scene
            reduced={reduced}
            ready={visible}
          />
        )}
      </div>
    </div>
  );
}
